import { ref } from 'vue';

import { useAudioStore } from '@/stores/audio.ts';
import { useMicrophoneStore } from '@/stores/microphone.ts';
import { useToastStore } from '@/stores/toast.js';
import { isAppleDevice } from '@/utils/device.ts';

export const androidGain = 0.95;
export const iosGain = 5.0;
export const androidMaxGain = 1.0;
export const iosMaxGain = 6.0;

let trueTimestamps = []; // 일정 시간 내에 low volume이 반복된 시각들
let cooldownLoud = false; // 클리핑 알림 쿨다운 (중복 방지)
let cooldownQuite = false; // low volume 알림 쿨다운 (중복 방지)
let toastStore;
let audioStore;
let toastDelay = 10000; // 알림 쿨다운 시간

export const clippingVoice = ref(false); // 음성 녹음 중 클리핑 여부
export const doAutoMaxGainControll = ref(true); // 자동 게인 조절 여부
export const toggleAutoMaxGainControll = () => {
  doAutoMaxGainControll.value = !doAutoMaxGainControll.value;
};

const cumulativeGains = []; // 최근 게인 값을 모아 평균 내기 위함

export const controllGain = (data, recordVoice = false) => {
  const audioStore = useAudioStore();
  const microphoneStore = useMicrophoneStore();
  if (audioStore.isMicOn || microphoneStore.isMicOn) {
    const currentMaxGain = findMax(data);
    cumulativeGains.push(currentMaxGain); // 새로운 값을 추가
    if (cumulativeGains.length > 10) cumulativeGains.shift(); // 배열의 크기가 초과되면 가장 오래된 값을 제거

    const avgGain = getAverage(); // 최근 10개 평균
    const lowVolume = detectLowVolume(data);
    const humanVoice = isHumanVoice(data, 16000);

    if (avgGain >= 0.05 && humanVoice) {
      if (!toastStore) toastStore = useToastStore();

      // 너무 멀리 있는 경우 (볼륨이 작음)
      if (lowVolume) {
        if (cooldownQuite) return;

        const now = Date.now();
        trueTimestamps.push(now);
        trueTimestamps = trueTimestamps.filter((ts) => now - ts <= 5000);

        if (trueTimestamps.length >= 2) {
          if (!recordVoice) toastStore.hintBox('mic', 'too_far_mic');
          trueTimestamps = [];
          cooldownQuite = true;
          setTimeout(() => {
            cooldownQuite = false;
            if (toastDelay === 10000 || toastDelay === 20000) toastDelay += 10000;
          }, toastDelay);
        }
      }

      // 너무 가까이 있는 경우 (클리핑 발생)
      else if (!cooldownLoud && isClippingSampled(data, 0.03, 0.5)) {
        if (recordVoice) {
          // 목소리 녹음시
          clippingVoice.value = true;
        } else {
          // 통역
          toastStore.hintBox('mic', 'too_near_mic');
          trueTimestamps = [];
          cooldownLoud = true;
          setTimeout(() => {
            cooldownLoud = false;
          }, 8000);
        }
      }
    }
  }
};

/**
 * 전체 마이크 입력 샘플 배열에서 일부(예: 20%)를 샘플링하여 클리핑 여부를 판별합니다.
 *
 * @param {Float32Array} samples - 전체 마이크 입력 샘플 배열
 * @param {number} thresholdRatio - 클리핑 비율 임계치 (기본값: 0.01, 즉 1%)
 * @param {number} samplePercentage - 전체 샘플 중 체크할 비율 (0.0 ~ 1.0, 기본값: 0.2, 즉 20%)
 * @returns {boolean} - 클리핑 비율이 thresholdRatio를 초과하면 true, 아니면 false
 */
function isClippingSampled(samples, thresholdRatio = 0.01, samplePercentage = 0.2) {
  // 고정 간격 샘플링: 예를 들어 20%면 stride는 약 5 (1/0.2)
  const stride = Math.max(1, Math.floor(1 / samplePercentage));
  let sampledCount = 0,
    clippingCount = 0;

  // 전체 데이터에서 stride 간격으로 샘플링
  for (let i = 0; i < samples.length; i += stride) {
    sampledCount++;
    if (Math.abs(samples[i]) > 1.0) clippingCount++;
  }

  return clippingCount / sampledCount > thresholdRatio;
}

// 최근 게인 평균 계산
function getAverage() {
  if (cumulativeGains.length === 0) return 0; // 배열이 비었을 경우 평균은 0
  return cumulativeGains.reduce((acc, val) => acc + val, 0) / cumulativeGains.length;
}

// 기기별 기본 게인값
export const deviceGain = () => (isAppleDevice() ? iosGain : androidGain);
export const deviceMaxGain = () => (isAppleDevice() ? iosMaxGain : androidMaxGain);

// 입력된 음성의 볼륨이 작은지 판별 (RMS → dB → 범위 검사)
function detectLowVolume(audioBuffer, sampleRate = 16000) {
  if (!audioBuffer || audioBuffer.length === 0) return false;

  // 1. RMS (Root Mean Square) 계산
  let sum = 0;
  for (let i = 0; i < audioBuffer.length; i++) {
    sum += audioBuffer[i] ** 2;
  }
  const rms = Math.sqrt(sum / audioBuffer.length);

  // 2. dB로 변환 (20 * log10(RMS))
  const db = 20 * Math.log10(rms);

  // 3. -25dB ~ -40dB 사이인지 확인
  return db >= -40 && db <= -25;
}

// 배열에서 가장 큰 값 (절댓값 기준)
export const findMax = (data) => {
  let max = -Infinity;
  for (let i = 0; i < data.length; i++) {
    if (Math.abs(data[i]) > max) max = data[i];
  }
  return max;
};

// 사람 목소리인지 판별 (자기상관 기반 pitch 추정)
function isHumanVoice(audioData, sampleRate = 16000) {
  const size = audioData.length;
  if (size === 0) return false;

  // 1. 신호의 총 에너지 계산 (lag 0)
  let energy0 = 0;
  for (let i = 0; i < size; i++) energy0 += audioData[i] ** 2;

  if (energy0 === 0) return false; // 완전 침묵인 경우

  // 2. 사람 목소리의 기본 주파수 범위 (대략 80Hz ~ 400Hz)에 해당하는 lag 계산
  const lagMin = Math.floor(sampleRate / 400); // 최대 주파수에 해당하는 최소 lag
  const lagMax = Math.floor(sampleRate / 80); // 최소 주파수에 해당하는 최대 lag

  // 3. 해당 lag 구간에서 자기상관값(정규화된)을 계산하여 최대 값을 찾는다.
  let bestCorr = 0,
    bestLag = 0;
  for (let lag = lagMin; lag <= lagMax; lag++) {
    let sum = 0;
    // lag에 대해 자기상관 계산: 신호와 lag만큼 이동한 신호의 내적
    for (let i = 0; i < size - lag; i++) {
      sum += audioData[i] * audioData[i + lag];
    }
    // 정규화: lag=0일 때의 에너지로 나누어줌
    const normCorr = sum / energy0;
    if (normCorr > bestCorr) {
      bestCorr = normCorr;
      bestLag = lag;
    }
  }

  // 4. 추정된 기본 주파수(pitch)를 계산 (sampleRate / lag)
  const estimatedPitch = sampleRate / bestLag;
  // 5. 임계치 검사: 자기상관값이 일정 수준(여기서는 0.5 이상)이고,
  // 추정된 pitch가 80Hz~400Hz 범위이면 사람 목소리로 판단
  return bestCorr > 0.5 && estimatedPitch >= 80 && estimatedPitch <= 400;
}
